import React from "react"
import { cn } from "design_system/libs"

// A spined book: the fallback for a coverless item (podcast, app, tool, or a codex record on a bookshelf).
// A gold-trimmed spine with the title running up it, so the shelf stays full and readable. Themeable via
// the BOOK_SPINE_THEMES registry (mirrors the shelf `props` theming): a theme controls the spine colour
// palette, the trim colour, and the label treatment. Extracted from book_cover.tsx so covers and spines
// can be themed and story-tested independently.

export interface BookSpineTheme {
  /** Deterministic spine background palette; a title hashes to one of these. */
  palette: string[]
  /** Trim colour for the top/bottom bands (Celtic gold by default). */
  trim: string
  /** Spine label text colour. */
  labelColor: string
  /** Extra classes applied to the label span (font/tracking/etc.). */
  labelClassName?: string
}

// Spine themes come from the shared element registry (element_themes.ts) so spines, covers, and vinyls stay
// consistent per element (earth / air / fire / water / spirit / gold / mono, plus `default`). A caller can
// still pass a custom BookSpineTheme object. Extend by adding an element in element_themes.ts.
import { SPINE_ELEMENT_THEMES, type ElementThemeName } from "./element_themes"

export const BOOK_SPINE_THEMES = SPINE_ELEMENT_THEMES
export type BookSpineThemeName = ElementThemeName
export const DEFAULT_BOOK_SPINE_THEME: BookSpineTheme = SPINE_ELEMENT_THEMES.default

// Deterministic spine colour from the title so a given book always looks the same on the shelf.
const spineColor = (name: string, palette: string[]): string => {
  let hash = 0
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  }
  return palette[hash % palette.length]
}

const resolveTheme = (
  theme?: BookSpineThemeName | BookSpineTheme
): BookSpineTheme => {
  if (!theme) return DEFAULT_BOOK_SPINE_THEME
  if (typeof theme === "string")
    return BOOK_SPINE_THEMES[theme] ?? DEFAULT_BOOK_SPINE_THEME
  return theme
}

export interface BookSpineProps {
  name: string
  subheader?: string | null
  /** A named built-in theme, or a custom BookSpineTheme. Defaults to `default`. */
  theme?: BookSpineThemeName | BookSpineTheme
  className?: string
}

/**
 * A spined book with a themeable colour palette, gold (or themed) trim, and the title running up the spine.
 * Used as the coverless fallback in BookCover and as the render for codex records on a bookshelf section.
 */
export const BookSpine: React.FC<BookSpineProps> = ({
  name,
  subheader,
  theme,
  className,
}) => {
  const t = resolveTheme(theme)
  return (
    <span
      data-testid="book-spine"
      className={cn(
        "flex h-[200px] w-[52px] flex-col items-center justify-between rounded-sm border border-black/40 px-1 py-3 shadow-md",
        "transition-transform duration-300 ease-out motion-safe:hover:-translate-y-2",
        className
      )}
      style={{
        color: t.labelColor,
        background: `linear-gradient(90deg, rgba(255,255,255,0.18), transparent 32%), ${spineColor(
          name,
          t.palette
        )}`,
      }}
    >
      <span
        className="h-[3px] w-full rounded shrink-0"
        style={{ backgroundColor: t.trim, opacity: 0.7 }}
      />
      <span
        className={cn(
          "overflow-hidden text-ellipsis whitespace-nowrap text-center text-sm [writing-mode:vertical-rl] rotate-180 block px-2 leading-tight"
        )}
      >
        <span
          className={cn(
            "overflow-hidden text-ellipsis whitespace-nowrap block uppercase font-sans",
            t.labelClassName
          )}
        >
          {name}
        </span>
        <span
          className={cn(
            "overflow-hidden text-ellipsis whitespace-nowrap block text-2xs font-sans text-white/60"
          )}
        >
          {subheader}
        </span>
      </span>
      <span
        className="h-[3px] w-full rounded shrink-0"
        style={{ backgroundColor: t.trim, opacity: 0.7 }}
      />
    </span>
  )
}

export default BookSpine
